import React, { useState, useEffect } from 'react';
import { Table, Input, Button, message, Row, Col, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getAllBanks } from '../../services/bankService';
import Page from '../../components/layout/Page';
import BankForm from '../../components/BankModal/BankForm';

const Banks = () => {
    const { t } = useTranslation();
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedBank, setSelectedBank] = useState(null);

    useEffect(() => {
        fetchBanks();
    }, []);

    const fetchBanks = async () => {
        try {
            setLoading(true);
            const data = await getAllBanks();
            setBanks(data);
        } catch (error) {
            console.error('Error fetching banks:', error);
            message.error(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    const getColumnSearchProps = dataIndex => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={node => {
                        this.searchInput = node;
                    }}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
    });

    const handleSearch = (selectedKeys, confirm) => {
        confirm();
        this.setState({
            searchText: selectedKeys[0],
        });
    };

    const handleReset = clearFilters => {
        clearFilters();
        this.setState({ searchText: '' });
    };

    const columns = [
        {
            title: t('bank.fields.name'),
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            ...getColumnSearchProps('name'),
        },
        {
            title: t('bank.fields.contactPerson'),
            dataIndex: 'contact_person',
            key: 'contact_person',
            sorter: (a, b) => a.contact_person?.localeCompare(b.contact_person),
            ...getColumnSearchProps('contact_person'),
        },
        {
            title: t('bank.fields.zipCode'),
            dataIndex: 'zip_code',
            key: 'zip_code',
            width: 100,
            sorter: (a, b) => a.zip_code?.localeCompare(b.zip_code),
            ...getColumnSearchProps('zip_code'),
        },
        {
            title: t('bank.fields.city'),
            dataIndex: 'city',
            key: 'city',
            sorter: (a, b) => a.city?.localeCompare(b.city),
            ...getColumnSearchProps('city'),
        },
        {
            title: t('common.actions'),
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button onClick={() => handleEdit(record)}>
                        {t('common.edit')}
                    </Button>                    
                    {/* <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                        danger
                    /> */}
                </Space>
            ),
        },
    ];

    const headerExtra = (
        <Row gutter={[8, 8]} align="middle">
            <Col xs={12} sm={12} md={12} lg={12}>
                <Input
                    placeholder={t('bank.search')}
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: '100%' }}
                />
            </Col>
            <Col xs={12} sm={12} md={12} lg={12}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleAdd()}
                    style={{ width: '100%' }}
                >
                    {t('bank.addBank')}
                </Button>
            </Col>
        </Row>
    );

    const filteredBanks = banks.filter(bank =>
        bank.name.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleEdit = (record) => {
        setSelectedBank(record);
        setIsModalVisible(true);
    };

    const handleAdd = () => {
        setSelectedBank(null);
        setIsModalVisible(true);
    };

    const handleDelete = (record) => {
        // implement delete logic here
    };

    return (
        <Page
            title={t('bank.title')}
            subtitle={t('bank.subtitle')}
            headerExtra={headerExtra}
        >
            <div style={{ 
                width: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
            }}>
                <Table
                    columns={columns}
                    dataSource={filteredBanks}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <BankForm
                visible={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setSelectedBank(null);
                }}
                onSuccess={() => {
                    setIsModalVisible(false);
                    setSelectedBank(null);
                    fetchBanks();
                }}
                initialValues={selectedBank}
            />
        </Page>
    );
};

export default Banks;
